// modifyOrderBy — VQE-Edit edit-action: replace the clauseText of one ORDER BY
// entry at a given index. SR-Edit does not expose a `modify-order-by` op (the
// orderBy list only supports add / remove); this module composes the change as
// a two-commit chain — `add-order-by` at the same logical position, then
// `remove-order-by` of the now-shifted original — refreshing the
// `statementId` against the post-add buffer so the second commit doesn't fail
// with `no statement '<old-id>' in document`.
//
// Position is preserved for *every* index, including `index === 0`:
// `add-order-by` with `afterIndex = index - 1` inserts the replacement just
// before the entry at `index` (for `index === 0`, `afterIndex = -1`, which
// `planListInsert` treats as "prepend before the first item"). The original,
// now shifted one slot right, is then removed at `index + 1`. This keeps the
// toggled key at its original sort position — direction toggles never reorder
// the ORDER BY list.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove ORDER BY.

import type { QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import { commitEdit, trackEditCommit, type CommitEditCtx } from '../commitEdit';

export type ModifyOrderByOptions = {
  readonly structure: QueryStructure;
  /**
   * Original statement startOffset, used to re-locate the (re-hashed)
   * statement id in the buffer between the two commits in this chain.
   * Statements before the edit don't shift, so the start offset is a stable
   * anchor for finding the statement post-edit.
   */
  readonly statementStartOffset: number;
};

/**
 * Replace ORDER BY entry at `index` with `newClauseText`, preserving its sort
 * position for every index. The replacement is inserted at
 * `afterIndex = index - 1` (for `index === 0` this is `-1`, which
 * `planListInsert` treats as a prepend), then the original — now shifted to
 * `index + 1` — is removed. Toggling a direction therefore never reorders the
 * ORDER BY list, including the primary (first) sort key.
 */
export async function modifyOrderBy(
  index: number,
  newClauseText: string,
  ctx: CommitEditCtx,
  options: ModifyOrderByOptions,
): Promise<void> {
  if (!Number.isInteger(index) || index < 0) {
    throw new Error(`modifyOrderBy: index must be a non-negative integer (got ${index})`);
  }
  const text = newClauseText.trim();
  if (text.length === 0) {
    throw new Error('modifyOrderBy: newClauseText must not be empty');
  }
  const items = options.structure.orderBy ?? [];
  if (items.length === 0) {
    throw new Error('modifyOrderBy: ORDER BY list is empty');
  }
  if (index >= items.length) {
    throw new Error(
      `modifyOrderBy: index ${index} out of range (ORDER BY list has ${items.length} item(s))`,
    );
  }

  // In-place replacement that preserves the entry's sort position for every
  // index. Insert the replacement at `afterIndex = index - 1` (for `index === 0`
  // this is `-1`, which `planListInsert` prepends before the first item), then
  // remove the original — now shifted one slot right — at `index + 1`.
  //
  // Abort the chain if the first commit didn't apply. `commitEdit` no longer
  // throws (P12 §4.2), so we must branch on its `CommitOutcome`: without the
  // add, indices never shifted, and blindly running `remove-order-by index + 1`
  // would delete the WRONG entry. The failed first commit already recorded its
  // own feedback toast.
  // Both chain commits suppress their per-step `vqe_edit_commit` events —
  // one direction toggle must count ONCE, as `modify-order-by`, not as an
  // add + a remove (2026-07-25 volume audit double-count). Failure events
  // stay per-step (never suppressed).
  const chainCtx: CommitEditCtx = { ...ctx, suppressCommitAnalytics: true };
  const added = await commitEdit(
    { op: 'add-order-by', clauseText: text, afterIndex: index - 1 },
    chainCtx,
  );
  if (added.status !== 'applied') return;
  const refreshed = refreshCtxAtStartOffset(chainCtx, options.statementStartOffset);
  if (!refreshed) return;
  const removed = await commitEdit({ op: 'remove-order-by', index: index + 1 }, refreshed);
  if (removed.status === 'applied') trackEditCommit('modify-order-by');
}

function refreshCtxAtStartOffset(
  ctx: CommitEditCtx,
  startOffset: number,
): CommitEditCtx | null {
  const buf = ctx.service.getParsedBuffer(ctx.documentId);
  if (!buf) return null;
  const stmt = buf.documentStructure.statements.find(
    (s) => s.range.startOffset === startOffset,
  );
  if (!stmt) return null;
  return { ...ctx, statementId: stmt.id };
}
